import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { NotfallService } from '../../../core/services/notfall.service';
import { NotfallKategorieDto } from '../../../core/models/notfall.model';
import { SeoService } from '../../../core/services/seo.service';

/**
 * Reihenfolge nach Akutheit/Häufigkeit statt alphabetisch (Spec A):
 * Wasser/Rohrbruch und akute Verstopfung zuerst, dann Heizung (Winter),
 * Strom, ausgesperrt, Glasbruch. Unbekannte Slugs landen stabil hinten.
 */
const AKUTHEITS_RANG: Record<string, number> = {
  wasserrohrbruch: 0,
  verstopfung: 1,
  heizungsausfall: 2,
  stromausfall: 3,
  tueroeffnung: 4,
  glasbruch: 5,
};

/**
 * `/notfall` — Express-Wizard (One-Pager § 3.2). Listet die fünf Notfall-
 * Kategorien als große Buttons, daneben einen Sicherheits-Hinweis-Block
 * (112-/Netzbetreiber-Kontakt). Klick führt nach `/notfall/{slug}`.
 *
 * Serverseitig laden wir die Kategorien per TransferState — der erste Render
 * zeigt sofort die richtigen Festpreise für den aktuellen Zeitfenster-Slot.
 */
@Component({
  selector: 'app-notfall-wizard',
  standalone: true,
  imports: [CommonModule, RouterLink, CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './notfall-wizard.component.html',
  styleUrl: './notfall-wizard.component.scss',
})
export class NotfallWizardComponent implements OnInit {
  private readonly notfall = inject(NotfallService);
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);

  readonly kategorien = signal<NotfallKategorieDto[]>([]);
  readonly loading = signal<boolean>(true);
  readonly errorMessage = signal<string | null>(null);

  /** Kategorien nach Akutheit sortiert (stabil, Fallback = Server-Reihenfolge). */
  readonly sortedKategorien = computed(() => {
    const rows = this.kategorien();
    const rang = (slug: string) => AKUTHEITS_RANG[slug] ?? Number.MAX_SAFE_INTEGER;
    return rows
      .map((kat, index) => ({ kat, index }))
      .sort((a, b) => rang(a.kat.slug) - rang(b.kat.slug) || a.index - b.index)
      .map((entry) => entry.kat);
  });

  ngOnInit(): void {
    this.seo.apply({
      title: 'Notfall — Sofort-Hilfe vom Handwerker | Craftly',
      description:
        'Wasserrohrbruch, Heizungsausfall, Türöffnung, Glasbruch, Verstopfung — ' +
        'transparenter Festpreis pro Notfall, verfügbar live in deiner PLZ.',
      path: '/notfall',
      type: 'website',
    });

    this.notfall.getKategorien().subscribe({
      next: (rows) => {
        this.kategorien.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(
          'Notfall-Übersicht konnte nicht geladen werden. Bitte versuche es kurz erneut.',
        );
        this.loading.set(false);
      },
    });
  }

  goToKategorie(slug: string): void {
    this.router.navigate(['/notfall', slug]);
  }
}
